import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { createStoreBase } from '@/presentation/stores/resources/storeBase'
import DependencyContainer from '@/presentation/configuration/DependencyContainer'
import { FetchAllLearningCoursesUseCase } from '@/core/useCases/learning/course/FetchAllLearningCoursesUseCase'
import { FetchLearningCourseUseCase } from '@/core/useCases/learning/course/FetchLearningCourseUseCase'
import { CreateLearningCourseUseCase } from '@/core/useCases/learning/course/CreateLearningCourseUseCase'
import { LearningCourse, LearningCourseInput } from '@/core/domain/learning/course/LearningCourse'
import { NabooError } from '@/core/infrastructure/services/NabooError'
import type { ILearningCourseUrlOptions } from '@/core/domain/learning/course/ILearningCourseUrlOptions'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import { UpdateLearningCourseStatusUseCase } from '@/core/useCases/learning/course/UpdateLearningCourseStatusUseCase.ts'
import type { LearningModule } from '@/core/domain/learning/module/LearningModule.ts'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus.ts'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import { useReferentialStore } from '@/presentation/stores/referential/referential.ts'
import { getCourseColorByAxis as getCourseColorByAxisUtil } from '@/presentation/utils/axisColors.ts'

export const useLearningCoursesStore = defineStore('learningCourses', () => {
  // Récupération des cas d'utilisation depuis le conteneur d'injection de dépendances
  const fetchAllCoursesUseCase = DependencyContainer.resolve<FetchAllLearningCoursesUseCase>(
    FetchAllLearningCoursesUseCase
  )
  const fetchCourseUseCase = DependencyContainer.resolve<FetchLearningCourseUseCase>(
    FetchLearningCourseUseCase
  )
  const createCourseUseCase = DependencyContainer.resolve<CreateLearningCourseUseCase>(
    CreateLearningCourseUseCase
  )
  const updateLearningCourseStatusUseCase =
    DependencyContainer.resolve<UpdateLearningCourseStatusUseCase>(
      UpdateLearningCourseStatusUseCase
    )

  // Inscription aux évènements des autres stores
  storeEventBus.useEventListener('learning:module:updated', ({ module }) => {
    updateModuleInCourse(module)
  })

  storeEventBus.useEventListener('learning:quizdiag:updated', (data) => {
    console.log('learning:quizdiag:updated', data)
    updateDiagQuizInCourse(data.quiz)
  })

  // Utilisation de la base commune pour les stores
  const storeBase = createStoreBase(['fetchAllCourses', 'fetchCourse', 'createCourse'])

  // État
  const learningCourses = ref<LearningCourse[]>([])
  const currentCourse = ref<LearningCourse | null>(null)

  const learningCourseById = ref<Record<number, LearningCourse>>({})

  // Actions
  /**
   * Récupère tous les cours disponibles.
   * @param options - Options d'URL pour la récupération des cours.
   * @returns La liste des cours récupérés.
   */
  async function fetchAllCourses(options?: ILearningCourseUrlOptions) {
    try {
      // Indication du chargement
      storeBase.loading.value.fetchAllCourses = true
      storeBase.resetError()

      // Appel du cas d'utilisation avec les options d'URL
      const fetchedCourses = await fetchAllCoursesUseCase.execute(options)

      learningCourses.value = fetchedCourses
      learningCourseById.value = Object.fromEntries(fetchedCourses.map((lc) => [lc.id, lc]))
      storeBase.updateLastUpdated()

      return fetchedCourses
    } catch (error) {
      handleError(error, 'Une erreur est survenue lors de la récupération des cours.')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.fetchAllCourses = false
    }
  }

  /**
   * Récupère un cours spécifique par son ID.
   * @param id - L'ID du cours à récupérer.
   * @param options - Options d'URL pour la récupération du cours.
   * @returns Le cours récupéré.
   */
  async function fetchCourse(id: number, options?: ILearningCourseUrlOptions) {
    try {
      // Indication du chargement
      storeBase.loading.value.fetchCourse = true
      storeBase.resetError()

      // Appel du cas d'utilisation avec l'ID et les options d'URL
      const course = await fetchCourseUseCase.execute(id, options)

      currentCourse.value = course
      learningCourseById.value[course.id] = course
      storeBase.updateLastUpdated()

      return course
    } catch (error) {
      handleError(error, `Une erreur est survenue lors de la récupération du cours ${id}.`)
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.fetchCourse = false
    }
  }

  /**
   * Crée un nouveau cours avec les données fournies.
   * @param courseData - Les données du cours à créer.
   * @returns Le cours créé.
   */
  async function createCourse(courseData: {
    title: string
    name: string
    domainId?: number
    status?: LearningStatus.Values
    order?: number
  }) {
    try {
      // Indication du chargement
      storeBase.loading.value.createCourse = true
      storeBase.resetError()

      // Préparation de l'objet d'entrée pour le cas d'utilisation
      const courseInput = new LearningCourseInput({
        title: courseData.title,
        name: courseData.name,
        domainId: courseData.domainId,
        status: courseData.status || LearningStatus.Values.DRAFT,
        order: courseData.order || 0,
        // Propriétés supplémentaires nécessaires pour la validation du schema
        id: 0, // ID temporaire qui sera remplacé par l'API
        learningModules: [] // Modules vides pour la création initiale
      })

      // Appel du cas d'utilisation
      const createdCourse = await createCourseUseCase.execute(courseInput)

      // Mise à jour de l'état
      learningCourseById.value[createdCourse.id] = createdCourse
      learningCourses.value.push(createdCourse)
      currentCourse.value = createdCourse
      storeBase.updateLastUpdated()

      return createdCourse
    } catch (error) {
      handleError(error, 'Une erreur est survenue lors de la création du cours.')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.createCourse = false
    }
  }

  async function updateLearningCourseStatus(courseId: number, status: LearningStatus.Values) {
    const course = learningCourses.value.find((course) => course.id === courseId)

    if (!course) {
      handleError(
        new NabooError({
          message: `Le cours avec l'ID ${courseId} n'a pas été trouvé.`,
          status: 404,
          name: 'LearningCoursesStoreError',
          code: 'COURSE_NOT_FOUND'
        }),
        `Le cours avec l'ID ${courseId} n'a pas été trouvé.`
      )
    }

    try {
      // Indication du chargement
      storeBase.loading.value.updateLearningCourseStatus = true
      storeBase.resetError()

      // Appel du cas d'utilisation pour mettre à jour le statut
      const updatedCourse = await updateLearningCourseStatusUseCase.execute(courseId, status)

      // Mise à jour de l'état
      learningCourseById.value[updatedCourse.id] = updatedCourse
      const index = learningCourses.value.findIndex((course) => course.id === courseId)
      if (index !== -1) {
        learningCourses.value[index] = updatedCourse
        currentCourse.value = updatedCourse
        storeBase.updateLastUpdated()
      }

      return updatedCourse
    } catch (error) {
      handleError(
        error,
        `Une erreur est survenue lors de la mise à jour du statut du cours ${courseId}.`
      )
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.updateLearningCourseStatus = false
    }
  }

  /**
   * Met à jour un module de cours dans le store.
   * @param updatedModule - Le module mis à jour.
   */
  function updateModuleInCourse(updatedModule: LearningModule) {
    // Logique pour trouver le parcours contenant ce module et mettre à jour le module
    const course = learningCourses.value.find((course) =>
      course.learningModules?.some((module) => module.id === updatedModule.id)
    )

    if (course && course.learningModules) {
      const moduleIndex = course.learningModules.findIndex(
        (module) => module.id === updatedModule.id
      )
      if (moduleIndex !== -1) {
        course.learningModules[moduleIndex] = updatedModule
        storeBase.updateLastUpdated()
      }
    }
  }

  /**
   * Met à jour un quiz de cours dans le store.
   * @param updatedQuiz - Le quiz mis à jour.
   */
  function updateDiagQuizInCourse(updatedQuiz: Quiz) {
    console.log('🔥 updateDiagQuizInCourse called with:', updatedQuiz)
    console.log('Current course in store:', currentCourse.value)

    if (!currentCourse.value) {
      console.log('⚠️ No current course found - cannot update quiz')
      return
    }

    // trouver la tache du parcours contenant ce quiz et mettre à jour le quiz dans le parcours courant
    let taskUpdated = false
    currentCourse.value.domain.tasks.forEach((task) => {
      console.log(
        'Checking task:',
        task.id,
        'with quizStart:',
        task.quizStart?.id,
        'quizEnd:',
        task.quizEnd?.id
      )

      if (
        updatedQuiz.type === QuizType.Values.DIAG_START &&
        task.quizStart?.id === updatedQuiz.id
      ) {
        console.log('✅ Updating quizStart for task:', task.id)
        task.quizStart = updatedQuiz
        taskUpdated = true
      }
      if (updatedQuiz.type === QuizType.Values.DIAG_END && task.quizEnd?.id === updatedQuiz.id) {
        console.log('✅ Updating quizEnd for task:', task.id)
        task.quizEnd = updatedQuiz
        taskUpdated = true
      }
    })

    if (taskUpdated) {
      console.log('✅ Quiz updated in course tasks')
      storeBase.updateLastUpdated()
    } else {
      console.log('⚠️ No matching task found for quiz update')
    }
  }

  // Helper pour gérer les erreurs
  function handleError(error: unknown, defaultMessage: string) {
    let nabooError: NabooError

    if (error instanceof NabooError) {
      nabooError = error
    } else if (error instanceof Error) {
      nabooError = new NabooError({
        message: defaultMessage,
        status: 500,
        name: 'LearningCoursesStoreError',
        code: 'LEARNING_COURSES_STORE_ERROR',
        originalError: error
      })
    } else {
      nabooError = new NabooError({
        message: defaultMessage,
        status: 500,
        name: 'LearningCoursesStoreError',
        code: 'LEARNING_COURSES_STORE_ERROR'
      })
    }

    storeBase.setError(nabooError)
  }

  // Getters
  const activeCourses = computed(() => {
    return learningCourses.value.filter((course) => course.isActive())
  })

  const coursesSlug = computed(() => {
    const courseNames = learningCourses.value.map((course) => course.nameSlug)
    return [...new Set(courseNames)]
  })

  const getColorSet = computed(() => (nameSlug: string) => {
    const colorSets = [
      { tag: 'bg-orange-300 text-orange-900' },
      { tag: 'bg-purple-300 text-purple-900' },
      { tag: 'bg-green-300 text-green-900' },
      { tag: 'bg-red-300 text-red-900' },
      { tag: 'bg-blue-300 text-blue-900' },
      { tag: 'bg-pink-300 text-pink-900' },
      { tag: 'bg-yellow-300 text-yellow-900' }
    ]

    const index = coursesSlug.value.indexOf(nameSlug)
    return colorSets[index] || colorSets[0]
  })

  const getCourseById = computed(() => (id: number): LearningCourse | undefined => {
    return learningCourseById.value[id]
  })

  /**
   * Récupère la couleur d'un cours basée sur son axe du référentiel
   * @param course Le cours d'apprentissage
   * @returns Un objet contenant les classes CSS pour les tags
   */
  const getCourseColorByAxis = computed(() => (course: LearningCourse) => {
    const referentialStore = useReferentialStore()
    const axis = referentialStore.getAxisFromDomainId(course.domain.id)

    return getCourseColorByAxisUtil(course, axis)
  })

  // Reset du store
  function reset() {
    learningCourses.value = []
    currentCourse.value = null
    storeBase.resetBase()
  }

  return {
    // État
    learningCourses,
    currentCourse,
    ...storeBase,

    // Actions
    fetchAllCourses,
    fetchCourse,
    createCourse,
    updateLearningCourseStatus,
    reset,

    // Getters
    activeCourses,
    getCourseById,
    coursesSlug,
    getColorSet,
    getCourseColorByAxis
  }
})
